import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';

import './Modal.less';

/**
 * Displays a basic accessible modal dialog.
 *
 * Uses react-modal (https://github.com/reactjs/react-modal).
 */
export function Modal({
  appElement, bodyOpenClassName, children, className, isOpen,
  onAfterClose, onAfterOpen, onClose, shouldCloseOnOverlayClick,
  overlayClassName, theme, ...props
}) {
  let myAppElement = appElement;
  if (typeof appElement === 'string') {
    myAppElement = document.querySelector(appElement);
  }

  // adding bodyOpenClassName this way since react modal doesn't set ReactModal__Body--open even if this is null
  // we have styles dependent on ReactModal__Body--open if we're not overriding bodyOpenClassName
  const bodyOpenClassNameProp = bodyOpenClassName ? { bodyOpenClassName } : {};

  return (
    <ReactModal
      appElement={myAppElement}
      isOpen={isOpen}
      onAfterClose={onAfterClose}
      onAfterOpen={onAfterOpen}
      onRequestClose={onClose}
      className={`Modal theme_${theme} ${className}`}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      overlayClassName={`ModalOverlay theme_${theme} ${overlayClassName}`}
      {...bodyOpenClassNameProp}
      {...props}
    >
      {children}
    </ReactModal>
  );
}

Modal.propTypes = {
  /**
   * Set this to properly hide your application from assistive screenreaders and
   * other assistive technologies while the modal is open.
  */
  appElement: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(HTMLElement),
  ]),
  /** Child elements or components. These will be displayed within the modal dialog. */
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  /**
   * String className to be applied to the body.
   * Note that this replaces ReactModal__Body--open, and Modal.less has styles targetting that.
  * */
  bodyOpenClassName: PropTypes.string,
  /** String className to be applied to the container element. */
  className: PropTypes.string,
  /** String indicating how the content container should be announced to screenreaders. */
  contentLabel: PropTypes.string,
  /** Boolean describing if the modal should be shown or not. */
  isOpen: PropTypes.bool,
  onAfterClose: PropTypes.func,
  onAfterOpen: PropTypes.func,
  /** Function that will be run when the modal is requested to be closed. */
  onClose: PropTypes.func,
  /** String className to be applied to the overlay. */
  overlayClassName: PropTypes.string,
  /** Whether to close the modal on overlay click */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /** Visual theme for the component. */
  theme: PropTypes.string,
};

Modal.defaultProps = {
  appElement: 'body',
  bodyOpenClassName: null,
  className: '',
  contentLabel: 'Modal',
  isOpen: true,
  onAfterClose: () => {},
  onAfterOpen: () => {},
  onClose: () => {},
  overlayClassName: '',
  shouldCloseOnOverlayClick: true,
  theme: 'default',
};

export default Modal;
